export const MAX_CARD_LEVEL = 10;

export const CARD_CUSTOMIZATION_LEVELS = {
  nickname: 2,
  nameColor: 3,
  frame: 4,
  emblem: 5,
  haloColor: 6,
  finish: 7,
  signature: 8,
  aura: 9,
  portrait: 10,
} as const;

export type CardCustomizationFeature = keyof typeof CARD_CUSTOMIZATION_LEVELS;
export const CARD_CUSTOMIZATION_FEATURES = [
  'nickname', 'nameColor', 'frame', 'emblem', 'haloColor', 'finish', 'signature', 'aura', 'portrait',
] as const satisfies readonly CardCustomizationFeature[];

export const CARD_COLOR_KEYS = ['ice', 'cyan', 'gold', 'rose', 'mint', 'violet', 'peach'] as const;
export type CardColor = (typeof CARD_COLOR_KEYS)[number];
export const CARD_COLOR_HEX: Record<CardColor, string> = {
  ice: '#f1f4ff',
  cyan: '#7ee7f7',
  gold: '#ffe08a',
  rose: '#ff9fc4',
  mint: '#91edc4',
  violet: '#d9c0ff',
  peach: '#ffd0ad',
};
export const CARD_FRAMES = ['precision', 'duo', 'ornate'] as const;
export const CARD_EMBLEMS = ['star', 'heart', 'moon', 'flame', 'crown', 'sword'] as const;
export const CARD_FINISHES = ['silk', 'prism', 'starlight'] as const;
export const CARD_AURAS = ['orbit', 'petals', 'constellation'] as const;
export const CARD_PORTRAITS = ['original', 'closeup', 'cinematic'] as const;
export const CARD_NICKNAME_MAX = 32;
export const CARD_SIGNATURE_MAX = 48;

export interface CardCustomization {
  nickname?: string | null;
  nameColor?: CardColor | null;
  frame?: (typeof CARD_FRAMES)[number] | null;
  emblem?: (typeof CARD_EMBLEMS)[number] | null;
  haloColor?: CardColor | null;
  finish?: (typeof CARD_FINISHES)[number] | null;
  signature?: string | null;
  aura?: (typeof CARD_AURAS)[number] | null;
  portrait?: (typeof CARD_PORTRAITS)[number] | null;
}

export function cardFeatureAtLevel(level: number): CardCustomizationFeature | null {
  return CARD_CUSTOMIZATION_FEATURES.find((feature) => CARD_CUSTOMIZATION_LEVELS[feature] === level) ?? null;
}

export function canCustomizeCard(level: number, feature: CardCustomizationFeature): boolean {
  return Number.isInteger(level) && level <= MAX_CARD_LEVEL && level >= CARD_CUSTOMIZATION_LEVELS[feature];
}

export function lockedCardCustomizationFeature(level: number, customization: CardCustomization): CardCustomizationFeature | null {
  return CARD_CUSTOMIZATION_FEATURES.find((feature) =>
    customization[feature] != null && !canCustomizeCard(level, feature),
  ) ?? null;
}

export function cardCustomizationChanges(saved: CardCustomization, draft: CardCustomization): CardCustomization {
  return CARD_CUSTOMIZATION_FEATURES.reduce<CardCustomization>((changes, feature) =>
    (saved[feature] ?? null) === (draft[feature] ?? null)
      ? changes
      : { ...changes, [feature]: draft[feature] ?? null },
  {});
}


export function cardCustomizationAtLevel(level: number, customization: CardCustomization): CardCustomization {
  const visible = { ...customization };
  for (const feature of CARD_CUSTOMIZATION_FEATURES) {
    if (!canCustomizeCard(level, feature)) delete visible[feature];
  }
  return visible;
}
